import React, { useState } from 'react';
import { Combobox, InputBase, Group, Text, Button, Paper, ActionIcon, Divider, Stack } from '@mantine/core';
import { useClient } from '../../context/ClientContext';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconX } from '@tabler/icons-react';
import ClientForm from '../clients/ClientForm';
import { Drawer } from '@mantine/core';
import { formatPhoneNumber } from '../../utils/phoneUtils';

function ClientLookup({ value, onChange, error }) {
  const [opened, setOpened] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { clients, searchQuery, setSearchQuery } = useClient();
  const [isCreatingNew, { open: openNewClient, close: closeNewClient }] = useDisclosure(false);

  const handleCreateNew = async (newClient) => {
    closeNewClient();
    if (onChange && newClient) {
      onChange(newClient.id);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchQuery('');
  };

  const selectedClient = clients.find(c => c.id === value);

  const formatClientPhone = (client) => {
    if (!client.area_code || !client.phone_number) return '';
    return formatPhoneNumber(`${client.area_code}${client.phone_number}`);
  };

  const displayValue = selectedClient 
    ? `${selectedClient.first_name} ${selectedClient.last_name} - ${formatClientPhone(selectedClient)}${selectedClient.email ? ` | ${selectedClient.email}` : ''}`
    : searchQuery;

  return (
    <>
      <Combobox
        onOptionSubmit={(val) => {
          onChange(val);
          setSearchQuery('');
          setOpened(false);
          setIsFocused(false);
        }}
        opened={opened}
        onChange={setOpened}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      >
        <Combobox.Target>
          <InputBase
            label="Client"
            required
            error={error}
            placeholder="Search for a client..."
            onClick={() => setOpened(true)}
            rightSection={
              selectedClient ? (
                <ActionIcon size="sm" variant="subtle" onClick={handleClear}>
                  <IconX size={14} />
                </ActionIcon>
              ) : (
                <Combobox.Chevron />
              )
            }
            value={displayValue}
            onChange={(e) => {
              if (selectedClient) {
                onChange('');
              }
              setSearchQuery(e.target.value);
              setOpened(true);
              setIsFocused(true);
            }}
          />
        </Combobox.Target>

        {isFocused && 
            <Combobox.Dropdown>
                <Combobox.Options>
                    { !searchQuery ? (
                        <Combobox.Empty>
                            <Stack gap="xs">
                                <Text size="sm">Search for a client by name, phone number or email</Text>
                                <Divider label="Or" />
                                <Button
                                    variant="light"
                                    size="xs"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={openNewClient}
                                >
                                    Create New Client
                                </Button>
                            </Stack>
                        </Combobox.Empty>
                    ) : clients.length > 0 ? (
                        clients.map((client) => (
                            <Combobox.Option value={client.id} key={client.id}>
                            <Text size="sm">{client.first_name} {client.last_name}</Text>
                            <Text size="xs" c="dimmed">
                                {formatClientPhone(client)} {client.email ? `| ${client.email}` : ''}
                            </Text>
                            </Combobox.Option>
                        ))
                    ) : (
                    <Combobox.Empty>
                        <Group justify="space-between">
                        <Text size="sm">No results found</Text>
                        <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconPlus size={14} />}
                            onClick={openNewClient}
                        >
                            Create New Client
                        </Button>
                        </Group>
                    </Combobox.Empty>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        }
      </Combobox>

      <Drawer
        opened={isCreatingNew}
        onClose={closeNewClient}
        title="Create New Client"
        position="right"
        size="xl"
      >
        <Paper p="md">
          <ClientForm onClose={handleCreateNew} />
        </Paper>
      </Drawer>
    </>
  );
}

export default ClientLookup; 